// src/components/tour-packages/forms/AiItineraryUpload.tsx
import { useEffect, useRef, useState } from "react";
import { FiUploadCloud, FiFileText, FiX, FiLoader, FiZap } from "react-icons/fi";

import { useAiImportTour } from "@/api/features/tours";
import type { AiImportOptions } from "@/api/features/tours/api";
import type { AiTourImportResult } from "@/api/features/tours/types";

const ACCEPT =
  ".pdf,.docx,.xlsx,.xls,.jpg,.jpeg,.png,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,image/png,image/jpeg";

const MAX_FILES = 10;
const MAX_SIZE = 10 * 1024 * 1024; // 10 MB (matches the server multipart limit)

const LOADER_STAGES = [
  "Uploading documents…",
  "Reading itinerary…",
  "Extracting cities, days & hotels…",
  "Pulling pricing & inclusions…",
  "Almost there — assembling the tour…",
];

type Props = {
  /** Called with the extracted data once processing succeeds. */
  onImported: (data: AiTourImportResult) => void;
  /** Select-option lists the AI should choose from. */
  options?: AiImportOptions;
  disabled?: boolean;
};

export default function AiItineraryUpload({
  onImported,
  options,
  disabled,
}: Props) {
  const [files, setFiles] = useState<File[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);
  const [stage, setStage] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const mutation = useAiImportTour({
    onSuccess: (data) => {
      onImported(data);
      setFiles([]);
      if (inputRef.current) inputRef.current.value = "";
    },
  });

  // Rotate the loader copy while the request is in flight.
  useEffect(() => {
    if (!mutation.isPending) {
      setStage(0);
      return;
    }
    const id = setInterval(
      () => setStage((s) => Math.min(s + 1, LOADER_STAGES.length - 1)),
      3500
    );
    return () => clearInterval(id);
  }, [mutation.isPending]);

  function addFiles(incoming: FileList | null) {
    if (!incoming) return;
    setLocalError(null);
    const next: File[] = [...files];
    for (const f of Array.from(incoming)) {
      if (next.length >= MAX_FILES) {
        setLocalError(`You can upload at most ${MAX_FILES} files.`);
        break;
      }
      if (f.size > MAX_SIZE) {
        setLocalError(`"${f.name}" is larger than 10 MB.`);
        continue;
      }
      // de-dupe by name+size
      if (next.some((x) => x.name === f.name && x.size === f.size)) continue;
      next.push(f);
    }
    setFiles(next);
  }

  function removeFile(idx: number) {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  const busy = mutation.isPending;
  const serverError =
    mutation.isError && mutation.error
      ? mutation.error.message || "Could not process the itinerary."
      : null;

  return (
    <>
      {/* Full-screen processing overlay */}
      {busy && (
        <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-4 bg-slate-900/60 backdrop-blur-sm">
          <FiLoader className="h-12 w-12 animate-spin text-white" />
          <p className="px-6 text-center text-sm font-medium text-white">
            {LOADER_STAGES[stage]}
          </p>
          <p className="text-xs text-white/70">
            This can take up to a minute — please don't close this tab.
          </p>
        </div>
      )}

      <div className="rounded-xl border border-dashed border-sky-300 bg-sky-50/40 p-4">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
          <FiZap />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-slate-800">
            Have an itinerary? Let AI fill the form
          </h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Upload your itinerary (PDF, Word, Excel, or images) and we'll
            pre-fill the cities, day-wise plan, hotels, inclusions and pricing.
            You can review and edit everything before saving.
          </p>

          {/* Dropzone */}
          <label
            className={`mt-3 flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-slate-300 bg-white px-4 py-5 text-center transition hover:border-sky-400 ${
              busy || disabled ? "pointer-events-none opacity-60" : ""
            }`}
          >
            <FiUploadCloud className="text-xl text-slate-400" />
            <span className="text-xs font-medium text-slate-600">
              Click to choose files
            </span>
            <span className="text-[11px] text-slate-400">
              Up to {MAX_FILES} files · 10 MB each · PDF, Word, Excel, JPG, PNG
            </span>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={ACCEPT}
              className="hidden"
              disabled={busy || disabled}
              onChange={(e) => addFiles(e.target.files)}
            />
          </label>

          {/* Selected files */}
          {files.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {files.map((f, idx) => (
                <li
                  key={`${f.name}-${idx}`}
                  className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs"
                >
                  <span className="flex min-w-0 items-center gap-2 text-slate-700">
                    <FiFileText className="flex-shrink-0 text-slate-400" />
                    <span className="truncate">{f.name}</span>
                    <span className="flex-shrink-0 text-slate-400">
                      {(f.size / 1024 / 1024).toFixed(1)} MB
                    </span>
                  </span>
                  {!busy && (
                    <button
                      type="button"
                      onClick={() => removeFile(idx)}
                      className="flex-shrink-0 text-slate-400 hover:text-slate-700"
                      aria-label={`Remove ${f.name}`}
                    >
                      <FiX />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {(localError || serverError) && (
            <p className="mt-2 text-xs font-medium text-rose-600">
              {localError || serverError}
            </p>
          )}

          {/* Action */}
          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              disabled={busy || disabled || files.length === 0}
              onClick={() => {
                setLocalError(null);
                mutation.mutate({ files, options });
              }}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-xs font-semibold text-white hover:bg-sky-700 disabled:opacity-60"
            >
              {busy ? (
                <>
                  <FiLoader className="animate-spin" /> Processing…
                </>
              ) : (
                <>
                  <FiZap /> Generate from itinerary
                </>
              )}
            </button>
            {busy && (
              <span className="text-xs text-slate-500">
                {LOADER_STAGES[stage]}
              </span>
            )}
          </div>
        </div>
      </div>
      </div>
    </>
  );
}
